import React, { useContext, useState, useCallback } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { useActiveWeb3React } from '../../hooks'

import { ButtonPrimary } from '../../components/Button'
import { useWalletModalToggle } from '../../state/application/hooks'

import IntroCard from 'components/IntroCard'
import { TYPE } from 'theme'
import axios from 'axios'
import { AutoColumn } from 'components/Column'
import { Text } from 'rebass'
import ReferralInputPanel from './ReferralInputPanel'
import { API_POOL_BASE_URL } from 'constants/links'
import { roundToDecimalPlace } from 'utils/data'
import { Helmet } from 'react-helmet'
export const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 14px;
  display: flex;
  gap: 2px;
`

export const StyledTabButton = styled.button<{ isActive?: boolean }>`
  ${({ theme }) => theme.flexRowNoWrap}
  border: none;
  align-items: center;
  justify-content: center;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: white;
  padding: 8px 16px;
  border-radius: 14px;
  font-weight: 400;
  font-size: 14px;
  transition: all 300ms;
  background: ${({ isActive }) => (isActive ? '#4AFC38' : 'transparent')};
  color: ${({ isActive }) => (isActive ? 'black' : 'white')};

  &:hover {
    color: #4afc38;
  }

  ${({ isActive }) =>
    isActive &&
    `
      &:hover {
        color: black !important;
      }
    `}
`

const ContainerStyled = styled.div`
  width: 100%;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 200px;
  @media (min-width: 502px) {
    padding: 10px;
  }
`

const GridContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: calc(55% - 8px) calc(45% - 8px);
  }
`

const SubContainer = styled.div<{ bg1: boolean }>`
  background: ${({ theme }) => theme.bg0};
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`
const SubContainerHeader = styled.div`
  border-radius: 14px; 
  background: ${({ theme }) => theme.bg1}
  padding: 20px;
`

const SubContainerBody = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.bg0};
`

const ConnectWalletContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  min-height: 25vh;
  background: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  width: 100%;

  div {
    max-width: 300px;
  }
`

const ItemContainerStyled = styled.div`
  grid-column: span 12;

  @media (min-width: 768px) {
    grid-column: span 6;
  }

  width: 100%;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
  background: rgb(23, 23, 23);
  border-radius: 16px;
  padding: 20px;

  h2 {
    margin-top: 12px;
    margin-bottom: 0;
  }
  span {
    font-size: 32px;
    font-weight: 600;
    color: #4afc38;
  }
  p {
    font-size: 10px;
    margin-bottom: 0;
  }
`
const ContainerPointsStyled = styled.div`
  max-width: 800px;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(12, 1fr); /* 12 columns, each taking equal width */
  gap: 12px; /* Gap between grid items */

  @media (min-width: 768px) {
    grid-template-columns: repeat(12, 1fr); /* Still 12 columns on desktop */
  }
`
const Testnet = () => {
  const { account, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const theme = useContext(ThemeContext)
  const [loading, setLoading] = useState(false)

  const [inputValue, setInputValue] = useState<string>('')
  const [username, setUsername] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')

  const [points, setPoints] = useState<number>(0)
  const [ranking, setRanking] = useState<number | null>(null)
  const [lastUpdate, setLastUpdate] = useState<string | null>(null)
  const [copyStatus, setCopyStatus] = useState<string | null>(null) // State to manage copy status
  const saveLink = () => {
    if (username) {
      const referralLink = `${window.location.origin}#/swap?referral=${username}`
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(referralLink)
          .then(() => {
            setCopyStatus('Copied!')
            setTimeout(() => setCopyStatus(null), 5000)
          })
          .catch(() => {
            // Fallback to manual copy if writeText fails
            fallbackCopyTextToClipboard(referralLink)
          })
      } else {
        // Fallback for older browsers
        fallbackCopyTextToClipboard(referralLink)
      }
    }
  }

  // Fallback function using document.execCommand('copy')
  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      if (successful) {
        setCopyStatus('Copied!')
      } else {
        setCopyStatus('Copy failed')
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err)
    }

    document.body.removeChild(textArea)
    setTimeout(() => setCopyStatus(null), 5000)
  }

  // Function to fetch username using axios
  const fetchUsername = useCallback(async () => {
    if (!account) return

    try {
      setLoading(true)
      const response = await axios.get(`${API_POOL_BASE_URL}/api/get-id`, {
        params: { address: account },
      })
      const ranking = await axios.get(`https://raw.githubusercontent.com/HyperSwapX/HS-PP/refs/heads/main/pp.json`)
      const lastUpdateDate = ranking.data.lastUpdate
      const options = { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }

      const formattedDate = new Date(lastUpdateDate).toLocaleDateString('en-US', options as any)
      setLastUpdate(`${formattedDate}`)

      // Extract points data from the ranking
      const pointsData = ranking.data.points

      const pointsArray = Object.entries(pointsData).sort(([, a]: any, [, b]: any) => b.pts - a.pts)

      const rank = pointsArray.findIndex(([walletId]) => walletId === account) + 1
      setRanking(rank ? rank : pointsArray.length + 1)
      // Get the points for the user's account or default to 0
      const userData = pointsData[account]
      const userPoints = userData ? userData.pts : 0 // Extract 'pts' or default to 0 if not found

      setPoints(userPoints) // Update the points state

      if (response.data.success) {
        setUsername(response.data.id)
      } else {
        setUsername(null)
      }
    } catch (error) {
      console.error('Failed to fetch username:', error)
    } finally {
      setLoading(false)
    }
  }, [account, setLoading])

  // Fetch username on mount
  React.useEffect(() => {
    fetchUsername()
  }, [fetchUsername, account])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const alphanumericRegex = /^[a-zA-Z0-9]*$/

    if (value.length <= 18 && alphanumericRegex.test(value)) {
      setInputValue(value)
    }
  }

  const sign = async () => {
    if (!library || !account) return

    setLoading(true)
    const signer = library.getSigner()
    const timestamp = Math.floor(Date.now() / 1000)
    const message = `Please sign this message to confirm that you are the owner. Timestamp: ${timestamp}`
    setErrorMsg('')
    try {
      const signedMessage = await signer.signMessage(message)

      const response = await axios.post(`${API_POOL_BASE_URL}/api/signature`, {
        address: account,
        id: inputValue,
        signature: signedMessage,
        message: message,
      })

      if (!response.data.success) {
        throw new Error('Failed to submit signature')
      }

      await fetchUsername()
    } catch (error: any) {
      setErrorMsg(error?.response?.data?.message ? error?.response?.data?.message : 'An error occurred.')
      console.error('Error signing or sending message:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Testnet | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Explore and test HyperSwap features in our Testnet environment. Access the faucet for test tokens and learn about the referral program to optimize your testing experience."
        />
        <meta
          name="keywords"
          content="HyperSwap testnet, test platform, faucet, referral program, DeFi testing, Hyperliquid blockchain"
        />
        <meta property="og:title" content="Testnet | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Welcome to the Testnet! Experiment with HyperSwap's features, access the faucet for test tokens, and explore our referral program."
        />
        <meta property="og:url" content={`${window.location.origin}/#/testnet`} />
      </Helmet>
      <ContainerStyled>
        <IntroCard variant={1} key="Testnet">
          <h1>Testnet Environment</h1>
          <p>
            Welcome to the Testnet! Here, you can explore and test our platform features. You’ll find information about
            the faucet to obtain test tokens and details about the referral program to enhance your experience. Enjoy
            experimenting in this testing environment!
          </p>
        </IntroCard>

        <>
          <GridContainer>
            <div>
              <SubContainer bg1={false}>
                <SubContainerHeader>
                  <h2 style={{ fontSize: '18px', marginTop: '0' }}>Referral Overview</h2>
                  <p style={{ fontSize: '12px', color: '#C3C5CB', marginBottom: 0 }}>
                    Sign up for the Points Program! <br />
                    <br />
                    To participate and start earning points, register with a referral ID. <br />
                    <br />
                    To accumulate points, perform actions on the testnet version of Hyperswap application.
                  </p>
                </SubContainerHeader>
                <SubContainerBody>
                  {account ? (
                    <AutoColumn gap={'md'}>
                      {username === null ? (
                        <>
                          <ReferralInputPanel
                            id="referral-input"
                            value={inputValue}
                            handleChange={handleChange}
                            error={!!errorMsg}
                            disabled={false}
                          />

                          {errorMsg && (
                            <p style={{ color: '#FF6871', margin: 0, fontSize: '12px' }}>Error: {errorMsg}</p>
                          )}
                          <ButtonPrimary
                            id="join-pool-button"
                            style={{ padding: 16 }}
                            as={'button'}
                            onClick={sign}
                            disabled={loading || inputValue.length === 0}
                          >
                            <Text
                              fontWeight={400}
                              fontSize={14}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              {loading ? 'Signing...' : 'Sign'}
                            </Text>
                          </ButtonPrimary>
                        </>
                      ) : (
                        <>
                          <ReferralInputPanel
                            id="referral-input"
                            value={username}
                            handleChange={handleChange}
                            error={!!errorMsg}
                            disabled={true}
                          />
                          <ButtonPrimary id="copy" style={{ padding: 16 }} as={'button'} onClick={saveLink}>
                            <Text
                              fontWeight={400}
                              fontSize={14}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              {copyStatus || 'Copy my referral link'}
                            </Text>
                          </ButtonPrimary>
                          <ContainerPointsStyled>
                            <ItemContainerStyled>
                              <span>{ranking ? ranking : 0}</span>
                              <h2>Your Rank</h2>
                            </ItemContainerStyled>
                            <ItemContainerStyled>
                              <span>{roundToDecimalPlace(`${points}`, '0')}</span>
                              <h2>Your Points</h2>
                              <p style={{ gridColumn: 'span 12', color: 'lightgrey' }}>
                                Last update: {lastUpdate && <> {lastUpdate}</>}{' '}
                              </p>
                            </ItemContainerStyled>
                          </ContainerPointsStyled>
                        </>
                      )}
                    </AutoColumn>
                  ) : (
                    <ConnectWalletContainer>
                      <div>
                        <TYPE.mediumHeader textAlign="center" color={theme.text3}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-user"
                            style={{ marginBottom: '.5rem' }}
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                            <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                          </svg>

                          <div>{'Connect your wallet to see the your referral info.'}</div>
                        </TYPE.mediumHeader>
                        <ButtonPrimary style={{ marginTop: '2em', padding: '8px 16px' }} onClick={toggleWalletModal}>
                          Connect a wallet
                        </ButtonPrimary>
                      </div>
                    </ConnectWalletContainer>
                  )}
                </SubContainerBody>
              </SubContainer>
            </div>
            <div>
              <SubContainer bg1={false}>
                <SubContainerHeader>
                  <h2 style={{ fontSize: '18px', marginTop: '0' }}>Faucet</h2>
                  <p style={{ fontSize: '12px', color: '#C3C5CB', marginBottom: 0 }}>
                    Here is the information regarding the faucet.
                  </p>
                </SubContainerHeader>

                <SubContainerBody>
                  <p style={{ marginTop: '0', color: '#c3c5cb' }}>
                    All tokens and transactions are for testing purposes only.
                    <br /> <br />
                    The application is currently operating on the testnet. To interact with the protocol, you will need
                    HYPE tokens.
                    <br />
                    <br />
                    <b style={{ color: '#4afc38' }}>We are not responsible for the faucet</b> and are currently
                    redirecting to one managed by another project within the ecosystem. <br />
                  </p>
                  <ButtonPrimary
                    id="claim-button"
                    style={{ padding: 16, marginTop: '20px' }}
                    as={'button'}
                    onClick={() => window.open('https://app.hypurr.fi/faucet', '_blank')}
                  >
                    Hypurr Faucet
                  </ButtonPrimary>
                </SubContainerBody>
              </SubContainer>
            </div>
          </GridContainer>
        </>
      </ContainerStyled>
    </>
  )
}
export default Testnet
